import { useKeycloak } from "@react-keycloak/web";
import { useLogout } from "../LogoutProvider";
import { useError } from "../ErrorProvider";
import { useServerDown } from "../ServerDownProvider";
import { ERROR_MESSAGE } from "../../utils/constants";
import { useRoleCheck } from "../../hooks/useRoleCheck";
import ApiError from "../ApiError";
import * as Sentry from "@sentry/react";

// Ensure only one Keycloak token refresh runs at a time across all calls
let refreshInFlightPromise = null;

const useApiWrapper = () => {
  const { keycloak } = useKeycloak();
  const { handleSessionExpired, handleLogout } = useLogout();
  const { showError } = useError();
  const { checkRole } = useRoleCheck();
  const { showServerDownDialog } = useServerDown();

  const fetchWithAuth = async (url, options = {}) => {
    try {
      if (!checkRole()) {
        return null;
      }

      if (keycloak.authenticated) {
        try {
          // Reuse an in-flight refresh; otherwise start a new one
          if (!refreshInFlightPromise) {
            refreshInFlightPromise = new Promise((resolve, reject) => {
              keycloak.updateToken(30)
                .then(result => {
                  // result can be true/false; both are valid
                  resolve(result);
                })
                .catch(err => {
                  // err may be “true”, “false”, or something non-Error
                  reject(
                    err instanceof Error
                      ? err
                      : new Error(`Keycloak updateToken rejected with non-error: ${String(err)}`)
                  );
                });
            });
          }
          await refreshInFlightPromise;
        } catch (tokenError) {
          console.error("Token refresh failed:", tokenError);
          handleSessionExpired();
          throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
        } finally {
          // Clear the in-flight promise once done
          refreshInFlightPromise = null;
        }
      } else {
        handleSessionExpired();
        throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
      }

      const token = keycloak.token;
      if (!token) {
        handleSessionExpired();
        throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
      }

      const headers = {
        Authorization: `Bearer ${token}`,
        ...options.headers,
      };

      const response = await fetch(url, {
        ...options,
        headers,
      });

      // ------------------------------------------------------------------
      // 1. SENTRY ERROR CAPTURING
      // ------------------------------------------------------------------
      const targetStatusCodes = [400, 403, 422, 500, 501, 503, 504];
      if (targetStatusCodes.includes(response.status)) {
        try {
          const clonedResponse = response.clone();
          const errorBodyText = await clonedResponse.text();

          const method = options.method || "GET";

          Sentry.withScope((scope) => {
            scope.setTransactionName(`${method} ${url}`);
            scope.setTag("api_error", "true");
            scope.setTag("status_code", response.status.toString());
            scope.setTag("api_path", url);

            scope.setContext("Request Details", {
              url: url,
              method: method,
              body: options.body ? String(options.body) : "No Data",
            });

            scope.setContext("Response Details", {
              body: errorBodyText || "Empty Body",
            });

            Sentry.captureException(new Error(`API Error ${response.status}: ${method} ${url}`));
          });
        } catch (sentryErr) {
          console.error("Failed to log API error to Sentry:", sentryErr);
        }
      }
      // ------------------------------------------------------------------

      // 2. YOUR EXISTING ERROR HANDLING LOGIC
      if (response.status === 403) {
        showError(ERROR_MESSAGE.UNAUTHORIZE_ACCESS);
        throw new ApiError(403, ""); // Throwing to exit the function with an error object with empty message as we already handled the unauthorized access
      }

      if (response.status === 401) {
        handleSessionExpired();
        throw new ApiError(401, ""); // Throwing to exit the function with an error object with empty message as we already handled the session expiry
      }

      const isBlob = options.responseType === "blob";
      const readErrorData = () =>
        isBlob
          ? response.text().then((t) => { try { return JSON.parse(t); } catch { return {}; } })
          : response.json();

      if (response.status === 404) {
        if (url.includes("/aa-profile/me")) {
          showError(ERROR_MESSAGE.UNAUTHORIZE_ACCESS_PROFILE, handleLogout);
        } else {
          const errorData = await readErrorData();
          const errorMessage = errorData.errorMessage || errorData.detail || "No Data Found";
          throw new ApiError(response.status, errorMessage);
        }
        return;
      }

      // Check for server errors (500, 502, 503, 504)
      if ([500, 502, 503, 504].includes(response.status)) {
        showServerDownDialog();
        throw new ApiError(response.status, "");
      }

      if (!response.ok) {
        const errorData = await readErrorData();
        const errorMessage = errorData.errorMessage || errorData.detail || "API request failed";
        throw new Error(errorMessage);
      }

      if (isBlob) return await response.blob();
      return await response.json();
    } catch (error) {
      console.error("API Error:", error.message);
      throw error;
    }
  };

  return { fetchWithAuth };
};

export default useApiWrapper;
